/**
 * TV-Mode UI bootstrap.
 *
 *   • Auto-Detect: ?ui=tv override, oder UA-Sniff für Smart-TV-Browser
 *     (Tizen/Samsung, WebOS/LG, SMART-TV/Sony/etc., Roku, NetCast).
 *     Setzt `body.zap-app--tv` als CSS-Hook.
 *   • Spatial-Navigation: Arrow-Keys finden den geometrisch nächsten
 *     fokussierbaren Nachbarn — funktioniert auf jedem [data-focusable]
 *     oder fokussierbaren Element (button, a, [tabindex]). Enter aktiviert.
 *   • Auto-Focus auf das erste sichtbare fokussierbare Element beim Boot,
 *     damit der TV-Remote sofort etwas zu tun hat.
 */

const TV_UA_RX = /\b(Tizen|WebOS|SMART-TV|SmartTV|Roku|NetCast|HbbTV|GoogleTV|AppleTV|crKey|Chromecast|BRAVIA)\b/i;

export function tvModeActive() {
  return document.body.classList.contains('zap-app--tv');
}

export function applyTvMode() {
  const force = new URLSearchParams(window.location.search).get('ui') === 'tv';
  const sniff = TV_UA_RX.test(navigator.userAgent);
  if (force || sniff) {
    document.body.classList.add('zap-app--tv');
    initSpatialNav();
    return true;
  }
  return false;
}

// --- Spatial Navigation ----------------------------------------------------

function isFocusable(el) {
  if (!(el instanceof Element)) return false;
  if (el.hidden) return false;
  if (el.disabled) return false;
  const ti = el.getAttribute('tabindex');
  if (ti != null && parseInt(ti, 10) < 0) return false;
  if (el.matches('input, select, textarea')) return false;   // those handle their own arrow keys
  // Visibility check: skip elements outside the viewport-visible tree.
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  return true;
}

function listFocusables() {
  const sel = '[data-focusable], button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])';
  return Array.from(document.querySelectorAll(sel)).filter(isFocusable);
}

/** Score: closer in the requested direction wins; perpendicular distance penalises. */
function scoreCandidate(curRect, candRect, dir) {
  const cx = curRect.left + curRect.width / 2;
  const cy = curRect.top  + curRect.height / 2;
  const ax = candRect.left + candRect.width / 2;
  const ay = candRect.top  + candRect.height / 2;
  const dx = ax - cx;
  const dy = ay - cy;
  let primary, perpendicular;
  switch (dir) {
    case 'right': if (dx <= 4) return Infinity; primary = dx;  perpendicular = Math.abs(dy); break;
    case 'left':  if (dx >= -4) return Infinity; primary = -dx; perpendicular = Math.abs(dy); break;
    case 'down':  if (dy <= 4) return Infinity; primary = dy;  perpendicular = Math.abs(dx); break;
    case 'up':    if (dy >= -4) return Infinity; primary = -dy; perpendicular = Math.abs(dx); break;
    default: return Infinity;
  }
  // Heavy penalty on perpendicular distance keeps movement on a "rail".
  return primary + perpendicular * 1.8;
}

function moveFocus(dir) {
  const cur = document.activeElement;
  const all = listFocusables();
  if (!cur || !all.includes(cur)) {
    all[0]?.focus();
    return;
  }
  const curRect = cur.getBoundingClientRect();
  let best = null;
  let bestScore = Infinity;
  for (const cand of all) {
    if (cand === cur) continue;
    const s = scoreCandidate(curRect, cand.getBoundingClientRect(), dir);
    if (s < bestScore) { bestScore = s; best = cand; }
  }
  if (best) {
    best.focus({ preventScroll: false });
    best.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

let inited = false;
function initSpatialNav() {
  if (inited) return;
  inited = true;

  const dirMap = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
  document.addEventListener('keydown', (e) => {
    if (!tvModeActive()) return;
    // Don't hijack arrow keys inside text inputs etc.
    const tag = (e.target && e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;

    const dir = dirMap[e.key];
    if (dir) {
      e.preventDefault();
      moveFocus(dir);
      return;
    }
    // Enter → activate. Most buttons fire on Enter natively, but we make
    // sure custom-focusable elements (libcards) also get a click event.
    if (e.key === 'Enter' || e.key === ' ') {
      const cur = document.activeElement;
      if (cur && cur.matches('[data-focusable]') && !cur.matches('button, a')) {
        e.preventDefault();
        cur.click();
      }
    }
    // Backspace / Escape: navigate "back" — for now, focus the sidebar's
    // active item (so the user can quickly retreat to the nav).
    if (e.key === 'Backspace' || e.key === 'Escape') {
      const navActive = document.querySelector('.nav__item.is-active');
      if (navActive && navActive !== document.activeElement) {
        e.preventDefault();
        navActive.focus();
      }
    }
  }, true);

  // Auto-focus the first focusable element on boot (so the TV remote has a
  // starting point). Wait one frame so the shell is rendered.
  requestAnimationFrame(() => {
    const first = listFocusables()[0];
    if (first && document.activeElement === document.body) first.focus();
  });
}
